<template>
  <div data-cy="start-action" class="start w-100">
    <template v-if="isScheduled">
      <schedule-details
        :page-id="pageId"
        :page-type="formattedPageType"
        :schedule="schedule"
        @countdownEnded="handleCountdownEnded"
      />
    </template>

    <div class="d-flex align-items-center justify-content-center">
      <template v-if="showPrice && isPresent(priceInfo)">
        <cta-row-price
          :preheader="priceInfo.pricePreheader"
          :priceText="priceInfo.priceText"
          :subText="priceInfo.priceSubText"
          :priceInterval="priceInfo.priceInterval"
        />
      </template>
      <template v-else-if="showProgress">
        <completion-percent :percent-complete="progress.percent_complete" />
      </template>

      <template v-if="isScheduled && isSubscribed">
        <template v-if="hasStarted()">
          <v-button primary class="flex-1" @click="goToNextPage">
            {{ subscribeButtonText }}
          </v-button>
        </template>
        <template v-else>
          <div class="d-flex flex-grow-1 align-items-center">
            <add-to-calendar
              :starts-at="schedule.starts_at"
              :ends-at="schedule.ends_at"
              :title="pageTitle"
              :url="pageUrl"
            />
          </div>
        </template>
      </template>
      <template v-else-if="isScheduled && isLockedOut && !isWithinGracePeriod">
        <v-button primary block disabled>
          {{ formattedPageType }} in progress
        </v-button>
      </template>
      <template v-else-if="!(isSubscribed && sequenceList.length === 0)">
        <v-button
          primary
          @click="handleClick"
          :is-loading="isLoading || !hasCheckedAuthentication"
        >
          {{ subscribeButtonText }}
          <v-icon
            v-if="hasCompletedProgress"
            data-cy="completion-check"
            class="ml-1"
            icon="done_all"
          />
        </v-button>
      </template>
    </div>

    <alert-dialog
      :title="
        `Are you sure you want to leave${
          isGenericPageType ? '' : ` this ${pageType}`
        }?`
      "
      :actions="[
        {
          title: 'Cancel',
          callback: closeExitConfirm
        },
        {
          title: 'Yes, leave',
          callback: onExitConfirm
        }
      ]"
      :show="showExitConfirm"
      @close="closeExitConfirm"
    >
      Your history and progress for this {{ pageType }} will be deleted. You can
      rejoin anytime.
    </alert-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"

import { ACTIONS } from "@/store/page-action-panel"

import EventBus, { EVENTS } from "@/lib/event-bus"
import HandleNextPage from "@/lib/mixins/handle-next-page"

import {
  isPresent,
  isEmpty,
  pluralize,
  getImageUrl,
  formatSlug,
  isBuilderContext
} from "@/lib/utils"
import {
  toDate,
  isSameDay,
  toTime,
  isPast,
  parseEventTime,
  timeBetween,
  isWithin,
  toDateTime
} from "@/lib/date-utils"

export default {
  mixins: [HandleNextPage],

  components: {
    AddToCalendar: () =>
      import("@/components/page-actions/schedule/AddToCalendar"),
    ScheduleDetails: () =>
      import("@/components/page-actions/schedule/ScheduleDetails"),
    CompletionPercent: () =>
      import("@/components/page-actions/progress/CompletionPercent"),
    CtaRowPrice: () => import("@/components/cta-row/Price"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    pageId: { type: [Number, String], required: true },
    pageTitle: { type: String, default: "Event" },
    pageType: { type: String, default: "Event" },
    text: { type: String, default: "Start" },
    schedule: { type: Object, default: () => ({}) },
    access: { type: Object, default: () => ({}) },
    action: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      isLoading: false,
      showExitConfirm: false
    }
  },

  watch: {
    hasCheckedAuthentication: {
      immediate: true,
      handler: function() {
        this.isLoading = true
        if (this.hasCheckedAuthentication) {
          this.fetchPageSubscribers(this.pageId).finally(() => {
            this.isLoading = false
          })
        }
      }
    },

    isSubscribed: {
      immediate: true,
      handler: function() {
        if (this.isSubscribed) {
          this.addAction({
            uid: ACTIONS.LEAVE_PROGRAM,
            pageId: this.pageId,
            icon: "logout",
            text: `Leave ${this.isGenericPageType ? "" : this.pageType}`,
            callback: () => (this.showExitConfirm = true)
          })
        } else {
          this.removeAction({
            pageId: this.pageId,
            uid: ACTIONS.LEAVE_PROGRAM
          })
        }
      }
    }
  },

  methods: {
    toDate,
    parseEventTime,
    pluralize,
    timeBetween,
    toTime,
    toDateTime,
    getImageUrl,
    isPresent,

    ...mapActions("pages", [
      "subscribe",
      "unsubscribe",
      "start",
      "fetchPageSubscribers",
      "fetchPageAccess"
    ]),

    ...mapMutations("pages", ["setPageCompletion", "setPageSequences"]),
    ...mapMutations("pageActionPanel", ["addAction", "removeAction"]),

    closeExitConfirm() {
      this.showExitConfirm = false
      this.$router.removeHash() // Removes action modal
    },

    onExitConfirm() {
      this.closeExitConfirm()

      this.isLoading = true
      this.unsubscribe(this.pageId)
        .then(({ uncompleted_pages }) => {
          uncompleted_pages.forEach(pageId => {
            this.setPageCompletion({ pageId, complete: false })
          })
        })
        .finally(() => (this.isLoading = false))
    },

    handleClick() {
      if (this.isAuthenticated) {
        if (this.isSubscribed) {
          if (isPresent(this.progress)) {
            this.goToNextPage()
          } else if (this.pageRequiresPurchase) {
            EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
              productIds: this.pageAccess.data.product_ids
            })
          } else {
            this.subscribeAndStart()
          }
        } else if (this.pageRequiresPurchase) {
          EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
            productIds: this.pageAccess.data.product_ids
          })
        } else {
          this.subscribeAndStart()
        }
      } else {
        this.$router.push("#auth")
      }
    },

    subscribeAndStart() {
      this.isLoading = true

      this.subscribe(this.pageId)
        .then(data => {
          const pageId = this.pageId

          this.setPageSequences({ pageId, data: { list: data.sequence } })
          this.goToNextPage()
          EventBus.$emit(EVENTS.PROMPT_PUSH_PERMISSION)
        })
        .catch(() => {})
        .finally(() => {
          this.isLoading = false
        })
    },

    handleCountdownEnded() {
      if (this.isSubscribed) {
        this.isLoading = true
        setTimeout(() => {
          this.goToNextPage()
          this.isLoading = false
        }, 1000)
      }
    },

    goToNextPage() {
      if (this.isScheduled && !this.hasStarted()) return

      this.handleNextPage()
    },

    hasStarted() {
      return this.isSameDay
        ? isPast(this.schedule.starts_at)
        : isSameDay(this.schedule.starts_at, new Date()) ||
            isPast(this.schedule.starts_at)
    }
  },

  computed: {
    ...mapState("notifications", { hasGrantedNotifications: "granted" }),
    ...mapState("notifications", ["canAsk"]),

    ...mapGetters(["app"]),
    ...mapGetters("auth", [
      "currentUser",
      "isAuthenticated",
      "hasCheckedAuthentication"
    ]),
    ...mapGetters("pages", [
      "getPageAccess",
      "isSubscribedToPage",
      "getPageSubscribers",
      "getPageSequence",
      "getPageProgress"
    ]),
    ...mapGetters("products", ["getAppProducts"]),

    isScheduled() {
      return isPresent(this.schedule) && isPresent(this.schedule.starts_at)
    },

    hasMadeProgress() {
      return isPresent(this.progress)
        ? this.progress.percent_complete > 0
        : false
    },

    hasCompletedProgress() {
      return isPresent(this.progress) && this.progress.percent_complete === 100
    },

    isSubscribed() {
      return this.isSubscribedToPage(this.pageId)
    },

    isLockedOut() {
      return (
        !this.isSubscribed &&
        this.isInProgress &&
        !this.action.allow_join_during
      )
    },

    isInProgress() {
      return this.hasStarted() && !this.hasEnded
    },

    isWithinGracePeriod() {
      return (
        this.hasStarted() &&
        this.action.grace_period_interval &&
        this.action.grace_period_unit &&
        isWithin(
          this.schedule.starts_at,
          this.action.grace_period_unit,
          this.action.grace_period_interval?.charAt(0)
        )
      )
    },

    pageSubscribers() {
      return this.getPageSubscribers(this.pageId)
    },

    pageAccess() {
      return this.getPageAccess(this.pageId)
    },

    sequence() {
      return this.getPageSequence(this.pageId)
    },

    progress() {
      return this.getPageProgress(this.pageId)
    },

    sequenceList() {
      return isPresent(this.sequence) ? this.sequence.list : []
    },

    pageRequiresPurchase() {
      return (
        isPresent(this.pageAccess) &&
        this.pageAccess.data.require_purchase &&
        !this.pageAccess.data.has_purchased
      )
    },

    subscriberAvatars() {
      const subscribers = isPresent(this.pageSubscribers)
        ? this.pageSubscribers.last_5
        : []

      return this.isSubscribed
        ? [this.currentUser, ...subscribers]
        : subscribers
    },

    hasEnded() {
      return isPast(this.schedule.ends_at)
    },

    isSameDay() {
      // TODO: Update to isWithin to prevent weirdness when duration causes endsAt to be the next day
      return isSameDay(this.schedule.starts_at, this.schedule.ends_at)
    },

    isStartingToday() {
      return isWithin(this.schedule.starts_at, 1, "day")
    },

    showPrice() {
      return isBuilderContext()
        ? this.access.require_purchase && this.access.products.length > 0
        : this.pageRequiresPurchase
    },

    // TODO: Spec
    showProgress() {
      if (this.isSubscribed && !this.hasCompletedProgress) {
        if (this.isScheduled) {
          if (this.hasStarted()) {
            return this.hasMadeProgress
          } else {
            return false
          }
        } else {
          return this.hasMadeProgress
        }
      } else {
        return false
      }
    },

    pageUrl() {
      return window.location.href
    },

    priceInfo() {
      const productsToPurchase = isBuilderContext()
        ? this.access.products.map(p => p.id)
        : this.pageAccess.data.product_ids || []

      if (isEmpty(productsToPurchase)) return {}

      const availableProducts = [
        ...this.getAppProducts,
        ...this.access.products
      ].filter(
        product => !product.is_hidden && productsToPurchase.includes(product.id)
      )

      const lowestPrice = Math.min(
        ...availableProducts.map(product => product.price_cents)
      )
      const productToShow = availableProducts.find(
        product => product.price_cents === lowestPrice
      )

      if (!productToShow) return {}

      const isMultiProduct = availableProducts.length > 1
      const isSubscription = productToShow.purchase_type === "recurring"

      return {
        pricePreheader: isMultiProduct ? "From" : null,
        priceText: `${productToShow.symbol}${productToShow.price_dollars}`,
        priceSubText: isSubscription ? "Cancel Anytime" : "Once Off",
        priceInterval: `${productToShow.currency}${
          isSubscription ? ` / ${productToShow.interval}` : ""
        }`
      }
    },

    formattedPageType() {
      return this.isGenericPageType && this.isScheduled
        ? "Event"
        : formatSlug(this.pageType)
    },

    subscribeButtonText() {
      if (this.isScheduled) {
        return this.hasEnded || (this.hasStarted() && this.isSubscribed)
          ? `View ${this.formattedPageType}`
          : this.pageRequiresPurchase
          ? "Purchase"
          : this.text
      } else {
        // TODO: specs
        if (this.isSubscribed) {
          if (isPresent(this.progress)) {
            return this.hasCompletedProgress ? "100% Complete" : "Continue"
          } else if (this.pageRequiresPurchase) {
            return "Purchase"
          } else {
            return this.text
          }
        } else {
          return this.pageRequiresPurchase ? "Purchase" : this.text
        }
      }
    },

    isGenericPageType() {
      return this.pageType === "page"
    }
  }
}
</script>
